import React, { useState, useEffect, useRef } from "react";
import { collection, getDocs, doc, getDoc, updateDoc } from "firebase/firestore";
import StatsCard from "./StatsCard";
import MoreDrillsCard from "./MoreDrillsCard";

export default function HomeSection(props) {
  const { auth, db, handleSectionChange } = props;
  const [stats, setStats] = useState({
    questionsAnsweredToday: 0,
    successRateToday: 0,
    successRateLast7Days: 0,
    successRateLast30Days: 0,
  });
  const [downloadedTopics, setDownloadedTopics] = useState([]);
  const [publicTopics, setPublicTopics] = useState([]);
  const [shareCodeInput, setShareCodeInput] = useState("");
  const [downloadMessage, setDownloadMessage] = useState("");
  const [message, setMessage] = useState(""); // Add message state

  // Helper to get local date string in YYYY-MM-DD
  const getLocalDateString = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().split("T")[0];
  };

  useEffect(() => {
    const fetchStatsAndTopics = async () => {
      if (!auth.currentUser) return;

      try {
        const userUID = auth.currentUser.uid;
        const userDocRef = doc(db, "users", userUID);
        const userData = (await getDoc(userDocRef)).data() || {};
        const downloaded = userData.downloadedTopics || [];

        // Fetch dailyProgress subcollection and aggregate all quizzes
        const dailyProgressCol = collection(db, "users", userUID, "dailyProgress");
        const dailyProgressSnap = await getDocs(dailyProgressCol);
        const dailyProgress = {};
        dailyProgressSnap.forEach(docSnap => {
          // Each docSnap.data() is an object: { [quizId]: {answeredQuestions, correctAnswers}, ... }
          const dayData = docSnap.data();
          let answered = 0;
          let correct = 0;
          Object.values(dayData).forEach(val => {
            answered += val.answeredQuestions || 0;
            correct += val.correctAnswers || 0;
          });
          dailyProgress[docSnap.id] = { answeredQuestions: answered, correctAnswers: correct };
        });

        const today = getLocalDateString(); // Use local date
        const todayStats = dailyProgress[today] || { answeredQuestions: 0, correctAnswers: 0 };

        const calculateSuccessRate = (days) => {
          let totalAnswered = 0;
          let totalCorrect = 0;
          for (let i = 0; i < days; i++) {
            const date = new Date();
            date.setDate(date.getDate() - i);
            const dateKey = date.toISOString().split("T")[0];
            const stats = dailyProgress[dateKey] || { answeredQuestions: 0, correctAnswers: 0 };
            totalAnswered += stats.answeredQuestions;
            totalCorrect += stats.correctAnswers;
          }
          return totalAnswered > 0 ? Math.round((totalCorrect / totalAnswered) * 100) : 0;
        };

        setStats({
          questionsAnsweredToday: todayStats.answeredQuestions,
          successRateToday:
            todayStats.answeredQuestions > 0
              ? Math.round((todayStats.correctAnswers / todayStats.answeredQuestions) * 100)
              : 0,
          successRateLast7Days: calculateSuccessRate(7),
          successRateLast30Days: calculateSuccessRate(30),
        });

        setDownloadedTopics(downloaded);
      } catch (error) {
        console.error("Failed to fetch user stats or topics:", error.message);
      }
    };

    const fetchPublicTopics = async () => {
      try {
        const officialQuizzesRef = collection(db, "officialQuizzes");
        const officialQuizzesSnapshot = await getDocs(officialQuizzesRef);

        const publicTopicsData = [];
        officialQuizzesSnapshot.docs.forEach(doc => {
          publicTopicsData.push({
            id: doc.id,
            name: doc.data().title,
            shareCode: doc.data().shareCode
          });
        });
        setPublicTopics(publicTopicsData);
      } catch (error) {
        console.error("Failed to fetch public topics:", error.message);
      }
    };

    fetchStatsAndTopics();
    fetchPublicTopics();
  }, [auth, db]);

  const handleDownloadTopic = async (topic) => {
    console.log("handleDownloadTopic called with topic:", topic); // Debugging
    try {
      const shareCodeRef = doc(db, "shareCodeLinks", topic.shareCode);
      console.log("shareCodeRef:", shareCodeRef); // Debugging
      const shareCodeSnap = await getDoc(shareCodeRef);
      console.log("shareCodeSnap:", shareCodeSnap); // Debugging

      if (shareCodeSnap.exists()) {
        const shareCodeData = shareCodeSnap.data();
        console.log("shareCodeData:", shareCodeData); // Debugging
        const quizId = shareCodeData.linksTo;
        const shareCode = topic.shareCode; // Get shareCode from topic
        console.log("quizId:", quizId, "shareCode:", shareCode); // Debugging

        if (shareCode.startsWith("o")) { // Check if shareCode starts with "o"
          const quizRef = doc(db, "officialQuizzes", quizId);
          console.log("quizRef:", quizRef); // Debugging
          const quizSnap = await getDoc(quizRef);
          console.log("quizSnap:", quizSnap); // Debugging

          if (quizSnap.exists()) {
            const quizData = quizSnap.data();
            const allowedUsers = quizData.allowedUsers || []; // Get the allowedUsers array
            console.log("User ID:", auth.currentUser.uid, "Allowed Users:", allowedUsers);

            if (allowedUsers === null || allowedUsers.includes(auth.currentUser.uid)) {
              console.log("User has permission to download this topic.");

              // --- Assumed Download Logic ---
              console.log("Attempting to download topic:", topic.name); // Debugging
              try {
                // Add logic here to download the topic
                // For example, you might fetch the questions and store them locally
                console.log("Download logic executed successfully for topic:", topic.name); // Debugging
                const userUID = auth.currentUser.uid;
                const userDocRef = doc(db, "users", userUID);
                const userData = (await getDoc(userDocRef)).data() || {};
                const downloaded = userData.downloadedTopics || [];
                const updatedTopics = [...downloaded, topic.shareCode];
                await updateDoc(userDocRef, { downloadedTopics: updatedTopics });
                setDownloadedTopics(updatedTopics);
                setMessage("Topic downloaded successfully!");
              } catch (downloadError) {
                console.error("Error during download process:", downloadError);
                setMessage("Error during download process.");
              }
              // --- End Assumed Download Logic ---

            } else {
              console.warn("You do not have permission to download this topic. User ID:", auth.currentUser.uid, "Allowed Users:", allowedUsers);
              setMessage("You do not have permission to download this topic.");
            }
          } else {
            console.error("Quiz not found");
            setMessage("Quiz not found");
          }
        } else {
          // ...existing code...
        }
      } else {
        console.error("Share code not found");
        setMessage("Share code not found");
      }
    } catch (error) {
      console.error("Error downloading topic:", error);
      setMessage("Error downloading topic");
    }
  };

  const handleDownloadWithShareCode = async (shareCodeInput) => {
    if (!auth.currentUser) {
      return "User is not authenticated!";
    }

    try {
      const userUID = auth.currentUser.uid;
      const userDocRef = doc(db, "users", userUID);
      const userData = (await getDoc(userDocRef)).data() || {};
      const downloaded = userData.downloadedTopics || [];

      // Check if the user already has the topic
      if (downloaded.includes(shareCodeInput)) {
        return "Topic already downloaded.";
      }

      // Check for read permission in shareCodeLinks
      const shareCodeDocRef = doc(db, "shareCodeLinks", shareCodeInput);
      const shareCodeDoc = await getDoc(shareCodeDocRef);

      if (shareCodeDoc.exists()) {
        const linkData = shareCodeDoc.data();

        // If it's a userQuiz, no need to check allowedUsers
        if (shareCodeInput.startsWith("u")) {
          const updatedTopics = [...downloaded, shareCodeInput];
          await updateDoc(userDocRef, { downloadedTopics: updatedTopics });
          setDownloadedTopics(updatedTopics);
          return "Topic downloaded successfully!";
        } else {
          const allowedUsers = linkData.allowedUsers || [];
          if (allowedUsers.includes(userUID)) {
            const updatedTopics = [...downloaded, shareCodeInput];
            await updateDoc(userDocRef, { downloadedTopics: updatedTopics });
            setDownloadedTopics(updatedTopics);
            return "Topic downloaded successfully!";
          } else {
            console.error("You do not have permission to download this topic. User ID:", userUID, "Allowed Users:", allowedUsers);
            return "You do not have permission to download this topic.";
          }
        }
      } else {
        console.error("ShareCode document does not exist.");
        return "ShareCode document does not exist.";
      }
    } catch (error) {
      console.error("Failed to download topic:", error.message);
      return "Failed to download topic. Please try again.";
    }
  };

  return (
    // make parent relative and add top padding so content sits below the centered stats card
    <div
      style={{
        position: "relative",
        paddingTop: 240,
        background: "#0F0F0F",
        animation: "fadeIn 0.5s ease-in-out",
        animationDelay: "0s", // Fade-in for the first card
        animationFillMode: "forwards",
        overflow: "auto", // Ensure scrolling works
        height: "100%", // Set scrollable window to 100vh
        marginBottom: 50, // Margin to make scrolling possible
        paddingBottom: 80 // Add padding to account for bottom menu bar
      }}
    >
      {/* centered stats card (absolute inside this relative parent) */}
      <StatsCard
        answeredToday={stats.questionsAnsweredToday}
        todayPercent={stats.successRateToday}
        last7Percent={stats.successRateLast7Days}
        last30Percent={stats.successRateLast30Days}
        onStartQuiz={() => handleSectionChange("quiz")}
        onAddQuestions={() => handleSectionChange("addQuestions")}
        className="fade-in-card"
      />

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          .fade-in-card {
            animation: fadeIn 0.5s ease-in-out;
            animation-delay: 1s; /* Ensure delay is applied */
            animation-fill-mode: forwards;
          }
        `}
      </style>

      {/* existing HomeSection content moved down via paddingTop */}
      <div style={{ padding: 50 }}>
        <div className="space-y-4">
          {/* 
          <div>
            <p>Questions Answered Today: {stats.questionsAnsweredToday}</p>
            <p>Success Rate Today: {stats.successRateToday}%</p>
            <p>Success Rate Last 7 Days: {stats.successRateLast7Days}%</p>
            <p>Success Rate Last 30 Days: {stats.successRateLast30Days}%</p>
          </div>
          */}
          {/* <div className="flex flex-row gap-4 justify-center" style={{ marginTop: "25px" }}>
            <button
              onClick={() => handleSectionChange("quiz")}
              className="flex-1 py-3 px-6 bg-[#232323] hover:bg-[#222] text-[#f8f8f5] rounded-2xl shadow-md transition-colors duration-300 text-sm"
              style={{ fontSize: "0.95rem" }}
            >
              Start Quiz
            </button>
            <button
              onClick={() => handleSectionChange("addQuestions")}
              className="flex-1 py-3 px-6 bg-[#232323] hover:bg-[#222] text-[#f8f8f5] rounded-2xl shadow-md transition-colors duration-300 text-sm"
              style={{ fontSize: "0.95rem" }}
            >
              Add Questions
            </button>
          </div> */}
          {/* More Drills Section */}
          <MoreDrillsCard
            publicTopics={publicTopics}
            downloadedTopics={downloadedTopics}
            handleDownloadTopic={handleDownloadTopic}
            handleDownloadWithShareCode={handleDownloadWithShareCode}
            style={{ marginBottom: 200 }} // Adjusted bottom margin for better scrollability
          />
        </div>
      </div>
    </div>
  );
}


